import type {VirtualPreset} from '../types/Api';

/**
 * Structural comparison of the `columnsSettings` + `gridSettings` legs of two
 * preset-like objects, ignoring `dataQueryId`.
 *
 * Compares `gridSettings` keys by strict equality and `columnsSettings` entries
 * (length + per-field, matched by `columnKey`) by strict equality. Shared by
 * `isVirtualPresetEqual` and `isCustomPresetDirty`.
 *
 * `columnsSettings` semantics (T20-134447):
 *
 * - Entries whose `columnKey` exists on only ONE side are IGNORED (definition
 *   drift): a column added to the grid definition after a preset was saved is
 *   missing from that preset forever, and a removed column lingers in old
 *   presets. Neither is a user edit — counting them made every pre-existing
 *   preset permanently dirty the moment a release added a column. Deliberate
 *   trade-off: edits to a drift column alone don't flip the dirty flag; saving
 *   the preset picks them up regardless.
 *
 * - SHARED columns compare per-field (visible/width/pinned) AND by RELATIVE
 *   ORDER. Entry order IS preset state: `transformDefinition` sorts columns by
 *   the preset's `columnsSettings` index (colIndexMap), and a column drag must
 *   PUT the virtual preset / dirty a custom preset so the new order persists
 *   (pinned by column-drag-reorder.spec.ts e2e).
 */
export const areColumnsAndSettingsEqual = (
  a: Pick<VirtualPreset, 'gridSettings' | 'columnsSettings'>,
  b: Pick<VirtualPreset, 'gridSettings' | 'columnsSettings'>
): boolean => {
  // gridSettings: shallow key comparison (all values are primitives or booleans)
  const aSettings = a.gridSettings;
  const bSettings = b.gridSettings;
  const settingsKeys = Object.keys(aSettings) as (keyof typeof aSettings)[];
  const bSettingsKeys = Object.keys(bSettings);

  if (settingsKeys.length !== bSettingsKeys.length) {
    return false;
  }

  for (const key of settingsKeys) {
    if (aSettings[key] !== bSettings[key]) {
      return false;
    }
  }

  // columnsSettings: keys on only one side are skipped (definition drift); shared
  // keys compare per-field AND by relative order — see doc block above.
  const aCols = a.columnsSettings;
  const bCols = b.columnsSettings;

  const aKeys = new Set(aCols.map((col) => col.columnKey));
  const bColsByKey = new Map(bCols.map((col) => [col.columnKey, col]));

  const aShared = aCols.filter((col) => bColsByKey.has(col.columnKey));
  const bShared = bCols.filter((col) => aKeys.has(col.columnKey));

  // Same key set on both filtered sides — lengths can only diverge on duplicate keys.
  if (aShared.length !== bShared.length) {
    return false;
  }

  for (let i = 0; i < aShared.length; i++) {
    const ac = aShared[i];
    const bc = bShared[i];
    if (
      // Relative order of shared columns diverged (column drag) — order is preset state.
      ac.columnKey !== bc.columnKey ||
      ac.visible !== bc.visible ||
      ac.width !== bc.width ||
      ac.pinned !== bc.pinned
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Shallow structural comparison of two VirtualPreset objects.
 *
 * Compares `dataQueryId` by value, plus `gridSettings`/`columnsSettings` via
 * `areColumnsAndSettingsEqual`.
 *
 * Replaces the heavy `deep-object-diff` library for this specific use case —
 * VirtualPreset has a known, flat shape that does not require recursive diffing.
 */
export const isVirtualPresetEqual = (a: VirtualPreset, b: VirtualPreset): boolean => {
  if (a === b) {
    return true;
  }

  if (a.dataQueryId !== b.dataQueryId) {
    return false;
  }

  return areColumnsAndSettingsEqual(a, b);
};
